import { useCallback } from "react";
import { useToast } from "@repo/ui";
import type { CreateStatusRequest } from "~/types/api";
import type { StatusUnion } from "../status-view";

export type StatusCodeChange = {
  oldCode: number;
  newCode: number;
};

type Props = {
  editedStatusCodes: StatusCodeChange[];
  setEditedStatusCodes: React.Dispatch<React.SetStateAction<StatusCodeChange[]>>;
  localStatuses: StatusUnion[];
  originalStatusesMap: Map<number, StatusUnion>;
};

export const useStatusCodeChanges = ({
  editedStatusCodes,
  setEditedStatusCodes,
  localStatuses,
  originalStatusesMap,
}: Props) => {
  const { showToast } = useToast();
  const resolveEditedStatusCodes = useCallback(
    (prevCode: number, nextCode: number) => {
      if (prevCode === nextCode) return;

      setEditedStatusCodes((prev) => {
        const map = new Map(prev.map((e) => [e.oldCode, e]));
        const existingEntry = [...map.values()].find((e) => e.newCode === prevCode);

        if (existingEntry) {
          map.delete(existingEntry.oldCode);
          if (existingEntry.oldCode !== nextCode) {
            map.set(existingEntry.oldCode, { oldCode: existingEntry.oldCode, newCode: nextCode });
          }
        } else if (map.has(prevCode)) {
          const existing = map.get(prevCode)!;
          if (existing.oldCode !== nextCode) {
            map.set(prevCode, { oldCode: prevCode, newCode: nextCode });
          } else {
            map.delete(prevCode);
          }
        } else {
          map.set(prevCode, { oldCode: prevCode, newCode: nextCode });
        }

        return Array.from(map.values());
      });
    },
    [setEditedStatusCodes]
  );

  const optimisticUpdateStatusCode = useCallback((failedCodes: Set<number>) => {
    let successStatuses: CreateStatusRequest[] = [];
    if (failedCodes.size === 0) {
      successStatuses = localStatuses as CreateStatusRequest[];
    } else {
      const statusMap = new Map<number, CreateStatusRequest>();
      localStatuses.forEach((status) => {
        const codeChange = editedStatusCodes.find((c) => c.newCode === status.code);
        if (codeChange && failedCodes.has(codeChange.oldCode)) {
          const original = originalStatusesMap.get(codeChange.oldCode);
          if (original) {
            statusMap.set(original.code, original as CreateStatusRequest);
          }
        } else {
          statusMap.set(status.code, status as CreateStatusRequest);
        }
      });
      failedCodes.forEach((code) => {
        const original = originalStatusesMap.get(code);
        if (original && !statusMap.has(code)) {
          statusMap.set(code, original as CreateStatusRequest);
        }
      });

      successStatuses = Array.from(statusMap.values());
    }
    return successStatuses;
  }, [localStatuses, editedStatusCodes, originalStatusesMap]);

  const showCodeChangeToast = useCallback(
    (successSet: Set<number>) => {
      if (successSet.size === 0) return;

      const updatedEntries = editedStatusCodes.filter(
        ({ oldCode, newCode }) => successSet.has(oldCode) && successSet.has(newCode)
      );
      if (updatedEntries.length === 0) return;

      const swaps: { a: StatusCodeChange; b: StatusCodeChange }[] = [];
      const nonSwaps: StatusCodeChange[] = [];
      const usedInSwap = new Set<number>();

      updatedEntries.forEach((entry) => {
        if (usedInSwap.has(entry.oldCode)) return;
        const mirror = updatedEntries.find(
          (e) => e.oldCode === entry.newCode && e.newCode === entry.oldCode
        );
        if (mirror) {
          swaps.push({ a: entry, b: mirror });
          usedInSwap.add(entry.oldCode);
          usedInSwap.add(mirror.oldCode);
        } else {
          nonSwaps.push(entry);
        }
      });

      const parts: string[] = [];

      swaps.forEach(({ a }) => {
        const [fromCode, toCode] =
          a.oldCode < a.newCode ? [a.oldCode, a.newCode] : [a.newCode, a.oldCode];

        const statusNowAtFrom = localStatuses.find((s) => s.code === fromCode);
        const statusNowAtTo = localStatuses.find((s) => s.code === toCode);
        const originalFrom = originalStatusesMap.get(fromCode);
        const originalTo = originalStatusesMap.get(toCode);

        const nameChangedFrom =
          statusNowAtFrom && originalFrom && statusNowAtFrom.name !== originalFrom.name;
        const nameChangedTo = statusNowAtTo && originalTo && statusNowAtTo.name !== originalTo.name;

        let msg = `${fromCode} ↔ ${toCode}`;
        if (nameChangedFrom && nameChangedTo) {
          msg += ` (renamed: ${fromCode} → "${statusNowAtFrom?.name}", ${toCode} → "${statusNowAtTo?.name}")`;
        } else if (nameChangedFrom) {
          msg += ` (${fromCode} renamed to "${statusNowAtFrom?.name}")`;
        } else if (nameChangedTo) {
          msg += ` (${toCode} renamed to "${statusNowAtTo?.name}")`;
        }
        parts.push(msg);
      });

      nonSwaps.forEach(({ oldCode, newCode }) => {
        const newStatus = localStatuses.find((s) => s.code === newCode);
        const originalStatus = originalStatusesMap.get(oldCode);
        const nameChanged = newStatus && originalStatus && newStatus.name !== originalStatus.name;
        parts.push(
          nameChanged
            ? `${oldCode} → ${newCode} (renamed to "${newStatus?.name}")`
            : `${oldCode} → ${newCode}`
        );
      });

      const toastMessage = `Updated: ${parts.join(", ")}`;
      showToast("success", toastMessage, "Status update successful");

      setEditedStatusCodes((prev) =>
        prev.filter(
          (e) => !updatedEntries.some((u) => u.oldCode === e.oldCode && u.newCode === e.newCode)
        )
      );
    },
    [editedStatusCodes, localStatuses, originalStatusesMap, showToast, setEditedStatusCodes]
  );

  return { resolveEditedStatusCodes, showCodeChangeToast, optimisticUpdateStatusCode };
};
